import googleDriveService from './googleDrive';

class AsanaService {
  constructor() {
    this.baseUrl = 'https://app.asana.com/api/1.0';
    this.accessToken = null;
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  // Get access token from localStorage if saved
  getStoredToken() {
    return localStorage.getItem('asana_access_token');
  }

  // Save access token
  saveToken(token) {
    localStorage.setItem('asana_access_token', token);
    this.accessToken = token;
  }

  // Clear token
  clearToken() {
    localStorage.removeItem('asana_access_token');
    this.accessToken = null;
  }

  // Make authenticated request to Asana API
  async makeRequest(endpoint, options = {}) {
    const token = this.accessToken || this.getStoredToken();
    
    if (!token) {
      throw new Error('No Asana access token found. Please authenticate first.');
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        throw new Error('Asana authentication failed. Please re-authenticate.');
      }
      throw new Error(`Asana API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  // Get user's workspaces
  async getWorkspaces() {
    return await this.makeRequest('/workspaces');
  }

  // Get projects in a workspace
  async getProjects(workspaceId) {
    return await this.makeRequest(`/workspaces/${workspaceId}/projects`);
  }

  // Get tasks in a project
  async getTasks(projectId, options = {}) {
    const params = new URLSearchParams({
      opt_fields: 'name,notes,completed,completed_at,due_on,assignee.name,tags.name,custom_fields,created_at,modified_at,memberships.section.name,attachments,followers.name,num_subtasks,subtasks,html_notes',
      ...options
    });
    
    return await this.makeRequest(`/projects/${projectId}/tasks?${params}`);
  }

  // Get single task with all details
  async getTaskDetails(taskId) {
    const params = new URLSearchParams({
      opt_fields: 'name,notes,html_notes,completed,completed_at,due_on,due_at,assignee.name,tags.name,custom_fields,created_at,modified_at,memberships.section.name,projects.name,attachments,followers.name,num_subtasks,subtasks,comments'
    });
    
    return await this.makeRequest(`/tasks/${taskId}?${params}`);
  }

  // Get task comments/stories
  async getTaskStories(taskId) {
    return await this.makeRequest(`/tasks/${taskId}/stories`);
  }

  // Get sections in a project
  async getProjectSections(projectId) {
    return await this.makeRequest(`/projects/${projectId}/sections`);
  }

  // Format Asana task data into markdown document
  formatTaskAsMarkdown(task, includeComments = false) {
    let markdown = `# ${task.name}\n\n`;
    
    // Status
    markdown += `**Status:** ${task.completed ? '✅ Completed' : '🔄 In Progress'}\n`;
    if (task.completed && task.completed_at) {
      markdown += `**Completed:** ${new Date(task.completed_at).toLocaleDateString()}\n`;
    }
    
    // Due date
    if (task.due_on) {
      markdown += `**Due Date:** ${task.due_on}\n`;
    }
    
    // Assignee
    if (task.assignee) {
      markdown += `**Assignee:** ${task.assignee.name}\n`;
    }
    
    // Tags
    if (task.tags && task.tags.length > 0) {
      markdown += `**Tags:** ${task.tags.map(tag => tag.name).join(', ')}\n`;
    }
    
    // Section
    if (task.memberships && task.memberships.length > 0) {
      const sections = task.memberships
        .filter(m => m.section && m.section.name)
        .map(m => m.section.name);
      if (sections.length > 0) {
        markdown += `**Section:** ${sections.join(', ')}\n`;
      }
    }
    
    markdown += '\n---\n\n';
    
    // Description
    if (task.html_notes) {
      markdown += '## Description\n\n';
      // Convert HTML to markdown (basic conversion)
      const htmlToMarkdown = task.html_notes
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<p>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<strong>/gi, '**')
        .replace(/<\/strong>/gi, '**')
        .replace(/<em>/gi, '*')
        .replace(/<\/em>/gi, '*')
        .replace(/<[^>]+>/g, ''); // Remove remaining HTML tags
      
      markdown += htmlToMarkdown + '\n\n';
    } else if (task.notes) {
      markdown += '## Description\n\n';
      markdown += task.notes + '\n\n';
    }
    
    // Custom fields
    if (task.custom_fields && task.custom_fields.length > 0) {
      markdown += '## Custom Fields\n\n';
      task.custom_fields.forEach(field => {
        if (field.display_value) {
          markdown += `- **${field.name}:** ${field.display_value}\n`;
        }
      });
      markdown += '\n';
    }
    
    // Subtasks
    if (task.subtasks && task.subtasks.length > 0) {
      markdown += '## Subtasks\n\n';
      task.subtasks.forEach(subtask => {
        markdown += `- ${subtask.completed ? '✅' : '⬜'} ${subtask.name}\n`;
      });
      markdown += '\n';
    }
    
    // Comments (if requested)
    if (includeComments && task.comments) {
      markdown += '## Comments\n\n';
      task.comments.forEach(comment => {
        markdown += `**${comment.created_by.name}** - ${new Date(comment.created_at).toLocaleDateString()}\n`;
        markdown += `${comment.text}\n\n`;
      });
    }
    
    // Footer
    markdown += '---\n\n';
    markdown += `*Created: ${new Date(task.created_at).toLocaleDateString()}*\n`;
    markdown += `*Last Modified: ${new Date(task.modified_at).toLocaleDateString()}*\n`;
    markdown += `*Source: Asana*\n`;
    
    return markdown;
  }

  // Format project summary as markdown
  formatProjectSummaryAsMarkdown(project, tasks) {
    let markdown = `# ${project.name}\n\n`;
    
    if (project.notes) {
      markdown += `${project.notes}\n\n`;
    }
    
    markdown += '## Summary\n\n';
    
    const completedTasks = tasks.filter(t => t.completed).length;
    const totalTasks = tasks.length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
    markdown += `- **Total Tasks:** ${totalTasks}\n`;
    markdown += `- **Completed:** ${completedTasks}\n`;
    markdown += `- **In Progress:** ${totalTasks - completedTasks}\n`;
    markdown += `- **Completion Rate:** ${completionRate}%\n\n`;
    
    // Group tasks by section
    const tasksBySection = {};
    tasks.forEach(task => {
      const sectionName = task.memberships && task.memberships[0]?.section?.name || 'No Section';
      if (!tasksBySection[sectionName]) {
        tasksBySection[sectionName] = [];
      }
      tasksBySection[sectionName].push(task);
    });
    
    // List tasks by section
    Object.entries(tasksBySection).forEach(([sectionName, sectionTasks]) => {
      markdown += `## ${sectionName}\n\n`;
      
      sectionTasks.forEach(task => {
        const status = task.completed ? '✅' : '🔄';
        const assignee = task.assignee ? ` - ${task.assignee.name}` : '';
        const dueDate = task.due_on ? ` (Due: ${task.due_on})` : '';
        
        markdown += `- ${status} **${task.name}**${assignee}${dueDate}\n`;
        
        if (task.notes) {
          const preview = task.notes.substring(0, 100).replace(/\n/g, ' ');
          markdown += `  ${preview}${task.notes.length > 100 ? '...' : ''}\n`;
        }
        markdown += '\n';
      });
    });
    
    markdown += '---\n\n';
    markdown += `*Generated: ${new Date().toLocaleString()}*\n`;
    markdown += `*Source: Asana Project*\n`;
    
    return markdown;
  }

  // Save task to Google Drive
  async saveTaskToGoogleDrive(task, folderId, includeComments = false) {
    const markdown = this.formatTaskAsMarkdown(task, includeComments);
    const fileName = `${task.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-asana.md`;
    
    const accessToken = await googleDriveService.getAccessToken();
    
    // Create the file metadata
    const metadata = {
      name: fileName,
      parents: [folderId],
      mimeType: 'text/markdown'
    };
    
    // Create multipart request
    const boundary = '-------314159265358979323846';
    const delimiter = "\r\n--" + boundary + "\r\n";
    const close_delim = "\r\n--" + boundary + "--";
    
    const multipartRequestBody =
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      'Content-Type: text/markdown\r\n\r\n' +
      markdown +
      close_delim;
    
    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'multipart/related; boundary="' + boundary + '"'
      },
      body: multipartRequestBody
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create document: ${response.status} ${response.statusText}`);
    }
    
    const document = await response.json();
    console.log('✅ Asana task saved to Google Drive:', document);
    
    return document;
  }

  // Sync entire project to Google Drive folder
  async syncProjectToGoogleDrive(projectId, folderId) {
    const results = {
      success: [],
      failed: [],
      summary: null
    };
    
    try {
      // Get project details
      const project = await this.makeRequest(`/projects/${projectId}`);
      
      // Get all tasks
      const tasks = await this.getTasks(projectId);
      
      // Create project summary
      const summaryMarkdown = this.formatProjectSummaryAsMarkdown(project, tasks);
      const summaryFileName = `${project.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-summary.md`;
      
      // Save summary
      try {
        const summaryDoc = await this.saveMarkdownToGoogleDrive(
          summaryMarkdown,
          summaryFileName,
          folderId
        );
        results.summary = summaryDoc;
      } catch (error) {
        console.error('Failed to save project summary:', error);
      }
      
      // Save individual tasks
      for (const task of tasks) {
        try {
          // Get full task details
          const taskDetails = await this.getTaskDetails(task.gid);
          const doc = await this.saveTaskToGoogleDrive(taskDetails, folderId);
          results.success.push({
            task: task.name,
            document: doc
          });
        } catch (error) {
          console.error(`Failed to save task ${task.name}:`, error);
          results.failed.push({
            task: task.name,
            error: error.message
          });
        }
      }
      
    } catch (error) {
      console.error('Project sync failed:', error);
      throw error;
    }
    
    return results;
  }

  // Helper method to save markdown content to Google Drive
  async saveMarkdownToGoogleDrive(content, fileName, folderId) {
    const accessToken = await googleDriveService.getAccessToken();
    
    const metadata = {
      name: fileName,
      parents: [folderId],
      mimeType: 'text/markdown'
    };
    
    const boundary = '-------314159265358979323846';
    const delimiter = "\r\n--" + boundary + "\r\n";
    const close_delim = "\r\n--" + boundary + "--";
    
    const multipartRequestBody =
      delimiter +
      'Content-Type: application/json\r\n\r\n' +
      JSON.stringify(metadata) +
      delimiter +
      'Content-Type: text/markdown\r\n\r\n' +
      content +
      close_delim;
    
    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'multipart/related; boundary="' + boundary + '"'
      },
      body: multipartRequestBody
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create document: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }
}

export default new AsanaService();